import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { BulkAction, BulkActionBarComponent } from 'src/app/shared/components/bulk-action-bar/bulk-action-bar.component';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { UserDocsAdminService, UserDocGroup } from 'src/app/shared/services/user-docs-admin.service';
import { SearchDoc } from '@shared/index';

@Component({
    selector: 'app-user-docs-tab',
    templateUrl: './user-docs-tab.component.html',
    styleUrls: ['./user-docs-tab.component.scss', '../../../../../../shared/styles/bulk-selection.scss'],
    providers: [ConfirmationService],
    imports: [RouterLink, DatePipe, 
        FormsModule,
        ButtonModule,
        TableModule,
        BulkActionBarComponent,
        CardModule,
        MessageModule,
        ConfirmDialogModule,
        TooltipModule,
        MenuModule
    ]
})
export class UserDocsTabComponent implements OnInit {
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'bulk-delete',
        label: $localize`:@@bulk-delete:Löschen`,
        icon: 'ri ri-delete-bin-line',
        command: () => this.deleteSelectedDocuments()
      }
    ];
  }

    protected groups: UserDocGroup[] = [];
    protected flatDocs: { userName: string; userId: string; doc: SearchDoc }[] = [];
    protected loading = true;
    public docContextMenu: MenuItem[] = [];
    public selectedDocuments: { userName: string; userId: string; doc: SearchDoc }[] = [];

    public get allSelected(): boolean {
        return this.flatDocs.length > 0 && this.selectedDocuments.length === this.flatDocs.length;
    }

    constructor(
        private userDocsAdminService: UserDocsAdminService,
        private toastr: ToastrService,
        private confirmationService: ConfirmationService,
        public rss: RouteShareService
    ) { }

    public ngOnInit(): void {
        void this.load();
    }

    protected async load(): Promise<void> {
        this.loading = true;
        try {
            const result = await this.userDocsAdminService.getUserDocuments(this.rss.botConfig._id);
            this.groups = result.grouped;
            this.flatDocs = this.groups.flatMap(g => g.docs.map(d => ({ userName: g.userName, userId: g.userId, doc: d })));
        } finally {
            this.loading = false;
        }
    }

    public openDocMenu(event: Event, menu: Menu, doc: SearchDoc): void {
        this.docContextMenu = this.buildDocMenuItems(doc);
        menu.toggle(event);
    }

    public toggleAll(checked: boolean): void {
        this.selectedDocuments = checked ? [...this.flatDocs] : [];
    }

    public deleteSelectedDocuments(): void {
        this.confirmationService.confirm({
            header: $localize`:@@user-docs-delete-header:Ausgewählte Dokumente löschen?`,
            message: $localize`:@@user-docs-delete-message:Möchtest du die ausgewählten Dokumente unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
            acceptLabel: $localize`:@@delete:Löschen`,
            rejectLabel: $localize`:@@cancel:Abbrechen`,
            accept: async () => {
                for (const item of this.selectedDocuments) {
                    await this.userDocsAdminService.deleteUserDocument(item.doc._id);
                }
                this.selectedDocuments = [];
                this.toastr.success('DELETED');
                await this.load();
            }
        });
    }

    private buildDocMenuItems(doc: SearchDoc): MenuItem[] {
        return [
            {
                label: $localize`:@@delete:Löschen`,
                icon: 'ri ri-delete-bin-line',
                command: () => { this.deleteDoc(doc._id, doc.filename); }
            }
        ];
    }

    protected deleteDoc(docId: string, filename: string): void {
        this.confirmationService.confirm({
            header: $localize`:@@user-doc-delete-header:Dokument löschen?`,
            message: $localize`:@@user-doc-delete-message:Möchtest du „${filename}" unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
            acceptLabel: $localize`:@@delete:Löschen`,
            rejectLabel: $localize`:@@cancel:Abbrechen`,
            accept: async () => {
                await this.userDocsAdminService.deleteUserDocument(docId);
                this.selectedDocuments = this.selectedDocuments.filter(item => item.doc._id !== docId);
                this.toastr.success('DELETED');
                await this.load();
            }
        });
    }
}
